import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import {Dialog, DialogActions, DialogTitle, DialogContent, DialogContentText, Button} from '@mui/material';

import './VirtualRouter.scss';
import Root from './Route';
import { BranchFunction, fn } from './Functions';
import ContentsAdmin from './Route/Contents/Admin';
import { useSession } from './Recoil/Session/Session';
import { useRouter } from './Recoil/Router/Router';
import { logBehavior } from './Data/Behavior';
import LoginHome from './Route/Login';
import MyPage from './Route/MyPage';
import Payment from './Route/Payment';


const jsxBranch = (path: string, query: string, hash: string, newProps: any) => BranchFunction<()=>ReactNode, {path: string, query?: string, hash?: string, newProps?: any}>(
    {path},
    [
        {
            validator: ({path}) => /^\/login/i.test(path) || /^\/logout/i.test(path),
            defaultValue: () => <LoginHome path={path} query={query}/>
        },
        {
            validator: ({path}) => /^\/mypage/i.test(path),
            defaultValue: () => <MyPage path={path} query={query}/>
        },
        {
            validator: ({path}) => path.toLowerCase().startsWith("/contents"),
            defaultValue: () => <ContentsAdmin {...{path: path.replace(new RegExp("^/contents"),""), query, hash, isClient: newProps.isClient}} />
        },
        {
            validator: ({path}) => path.toLowerCase().startsWith("/payment"),
            sublogic: [
                {
                    validator: ({path}) => Boolean(new RegExp("/payment/([^/]+)/([^/]+)/?([^/]+)?").exec(path.toLowerCase())),
                    defaultValue: () => {
                        let [,serviceId = "", titleId = "", menu = undefined] = [...(new RegExp("/payment/([^/]+)/([^/]+)/?([^/]+)?").exec(path) || [])] as [any, string, string|undefined, string|undefined, string];
                        return <Payment {...{serviceId, titleId, menu}} />
                    }
                }
            ],
            defaultValue: () => {
                return <Root />
            }
        }
    ],
    () => <Root />,
)

const VirtualRouter = (props: {
    isClient: boolean, injectedLocation?: string,
}) => {
    const { session, sessionRefresh } = useSession();
    const {pushRedirectTo, popRedirectTo} = useRouter();

    useEffect(()=>{
        (async()=>{
            await sessionRefresh();
        })();
    }, [sessionRefresh])
    
    /**
     * @type {[
     *      [string, set<string>],
     *      [size, set<size>],
     *      [number, set<number>],
     * ]}
     * */
    const [path, setPath] = useState((props.injectedLocation || window.location.pathname).replace(window.location.origin, ""));
    const [query, setQuery] = useState('?' + (props.injectedLocation?.split('?')[1]?.split('#')[0] || window.location.search.slice(1)));
    const [hash, setHash] = useState((props.injectedLocation || '').split('#')[1] || window.location.hash);

    useEffect(()=>{
        const historyListener = ()=>{
            setPath(window.location.pathname);
            setQuery(window.location.search);
            setHash(window.location.hash);
        };
        window.addEventListener('popstate', historyListener);

        const searchParams = new URL(window.location.href).searchParams;
        if (searchParams.has('redirectTo')){
            pushRedirectTo(searchParams.get('redirectTo') ?? "");
        }

        return ()=> {
            window.removeEventListener('popstate', historyListener);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    const handleUrlOrPath = useCallback((stateHandling: any) => (urlOrPath: string | null | undefined) => {
        if (urlOrPath === window.location.pathname){
            return;
        }
        logBehavior('goto', {to: urlOrPath});

        if (urlOrPath === undefined || urlOrPath === null) {
            window.history.back();
        }else if (urlOrPath.startsWith("/")){
            if ((window as any).gtag){
                (window as any).gtag('config', "G-8PRR5WLG9M", {'page_path': urlOrPath});
            }            
            
            const path = urlOrPath.split('#')[0];
            const hash = urlOrPath.split('#')[1];
            
            if (path.startsWith("/") || path === ""){
                setPath(path);
                setQuery('?' + (path.split('?')[1] || ''));
                setHash(hash);

                let query = new URL('https://'+window.location.host + path).searchParams;

                if (query.has('redirectTo')){
                    pushRedirectTo(query.get('redirectTo') ?? "");
                }

                if(stateHandling === 'push'){
                    window.history.pushState({}, document.title, window.location.origin + path + (hash?`#${hash}`:``));
                } else{
                    window.history.replaceState({}, document.title, window.location.origin + path + (hash?`#${hash}`:``));
                }
            }
            window.scrollTo(0,0);
        }else {
            window.open(urlOrPath);
        }
    }, [pushRedirectTo]);

    const goto = handleUrlOrPath('push');
    const replaceWith = handleUrlOrPath('replace');

    useEffect(()=>{
        fn.goto = goto;
        fn.replaceWith = replaceWith;
        
    },[popRedirectTo, goto, replaceWith]);

    const [
        [warningShown, setWarningShown],
        [warningTitle, setWarningTitle],
        [warningMessage, setWarningMessage],
        [warningShownCallback, setWarningShownCallback],
        [warningOKText, setWarningOkText],
        [warningCancelText, setWarningCancelText],
    ] = [
        useState<boolean>(false),
        useState<string>(""),
        useState<string>(""),
        useState<Function>((isOkay: boolean) => {}),
        useState<string>(""),
        useState<string>(""),
    ];
    

    const [showWarning,] = useState(()=>(warningTitle:string, warningMessage:string, warningShownCallback:Function, warningOKText:string, warningCancelText:string) => {
        setWarningTitle(warningTitle);
        setWarningMessage(warningMessage);
        setWarningShownCallback(warningShownCallback);
        setWarningOkText(warningOKText);
        setWarningCancelText(warningCancelText);
    });

    const newProps = {
        isClient: props.isClient,
        goto, path,
        showWarning,
        session: session
    };

    return (
    <>
        <div className={"Router"}>
            <div style={{width:'100%'}}>
                {jsxBranch(path, query, hash, newProps)()}
            </div>
        </div>
        <Dialog
            open={warningShown}
        >
            <DialogTitle>{warningTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText>{warningMessage}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={()=>{
                    setWarningShown(false)
                    warningShownCallback(false)
                }} >
                    {warningCancelText || "Close"}
                </Button>
                <Button onClick={()=>{
                    setWarningShown(true)
                    warningShownCallback(true)
                }} color="primary">
                {warningOKText || "OK"}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
}

export default VirtualRouter